import { useState } from "react";
import { Button } from "../ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { useDeleteUser, useGetUserProfile } from "@/tanstack-hooks";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import SpinnerSmall from "../SpinnerSmall";
import { AccountDeletionReason, UserGender } from "@/generated-api";
import SelectableOption from "../payments/SelectableOption";
import { AnimatePresence, motion } from "framer-motion";
import { Textarea } from "../ui/textarea";

enum OffboardingDialogStep {
  REASON_SELECTION = 1,
  DESCRIPTION = 2,
  PERSUASION = 3,
  CONFIRMATION = 4,
}

function ReasonDescription({
  reasonDescription,
  setReasonDescription,
  closeModal,
  setStep,
  t,
}: {
  reasonDescription: string;
  setReasonDescription: (v: string) => void;
  closeModal: () => void;
  setStep: (s: OffboardingDialogStep) => void;
  t: ReturnType<typeof useTranslation>;
}) {
  return (
    <>
      <DialogHeader className="text-left mb-4">
        <DialogTitle className="text-white flex flex-row items-center">
          {t.OFFBOARDING_FORM.LABELS.IN_A_FEW_WORDS_WHY_TITLE}
        </DialogTitle>
      </DialogHeader>
      <Textarea
        placeholder={t.OFFBOARDING_FORM.LABELS.MIN_TEN_CHARS_PLACEHOLDER}
        className="mb-4 text-base resize-none outline-none rounded-xl"
        minLength={10}
        maxLength={250}
        value={reasonDescription}
        onChange={(e) => setReasonDescription(e.target.value)}
      />
      <DialogFooter>
        <div className="w-full flex flex-row justify-between">
          <Button variant="secondary" onClick={closeModal}>
            {t.SHARED.LABELS.CANCEL_BUTTON}
          </Button>
          <Button
            disabled={
              !reasonDescription.trim() || reasonDescription.length < 10
            }
            variant="destructive"
            onClick={() => setStep(OffboardingDialogStep.CONFIRMATION)}
          >
            {t.OFFBOARDING_FORM.LABELS.CONTINUE_DELETING_BUTTON}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function ReasonSelection({
  selectedReason,
  setSelectedReason,
  userProfile,
  closeModal,
  setStep,
  t,
}: {
  selectedReason: AccountDeletionReason | null;
  setSelectedReason: (r: AccountDeletionReason) => void;
  userProfile: ReturnType<typeof useGetUserProfile>["data"];
  closeModal: () => void;
  setStep: (s: OffboardingDialogStep) => void;
  t: ReturnType<typeof useTranslation>;
}) {
  return (
    <>
      <DialogHeader className="text-left">
        <DialogTitle className="text-white flex flex-row items-center">
          {t.OFFBOARDING_FORM.LABELS.WHY_LEAVING_TITLE}
        </DialogTitle>
        <DialogDescription>
          {t.OFFBOARDING_FORM.LABELS.CHOOSE_REASON_DESCRIPTION}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-2 py-4 overflow-y-scroll">
        <SelectableOption
          planName={t.OFFBOARDING_FORM.LABELS.FOUND_ON_PULSE}
          isSelected={
            selectedReason === AccountDeletionReason.FOUND_RELATIONSHIP_PULSE
          }
          onClick={() =>
            setSelectedReason(AccountDeletionReason.FOUND_RELATIONSHIP_PULSE)
          }
        />
        <SelectableOption
          planName={t.OFFBOARDING_FORM.LABELS.FOUND_ELSEWHERE}
          isSelected={
            selectedReason === AccountDeletionReason.FOUND_RELATIONSHIP_OTHER
          }
          onClick={() =>
            setSelectedReason(AccountDeletionReason.FOUND_RELATIONSHIP_OTHER)
          }
        />
        <SelectableOption
          planName={t.OFFBOARDING_FORM.LABELS.TAKING_BREAK}
          isSelected={selectedReason === AccountDeletionReason.TAKING_BREAK}
          onClick={() => setSelectedReason(AccountDeletionReason.TAKING_BREAK)}
        />
        <SelectableOption
          planName={t.OFFBOARDING_FORM.LABELS.NOT_ENOUGH_MESSAGES}
          isSelected={
            selectedReason === AccountDeletionReason.NOT_ENOUGH_MESSAGES
          }
          onClick={() =>
            setSelectedReason(AccountDeletionReason.NOT_ENOUGH_MESSAGES)
          }
        />
        {userProfile?.gender === UserGender.MALE && (
          <SelectableOption
            planName={t.OFFBOARDING_FORM.LABELS.TOO_EXPENSIVE}
            isSelected={selectedReason === AccountDeletionReason.TOO_EXPENSIVE}
            onClick={() =>
              setSelectedReason(AccountDeletionReason.TOO_EXPENSIVE)
            }
          />
        )}
        <SelectableOption
          planName={t.OFFBOARDING_FORM.LABELS.OTHER}
          isSelected={selectedReason === AccountDeletionReason.OTHER}
          onClick={() => setSelectedReason(AccountDeletionReason.OTHER)}
        />
      </div>

      <DialogFooter>
        <div className="w-full flex flex-row justify-between">
          <Button variant="secondary" onClick={closeModal}>
            {t.SHARED.LABELS.CANCEL_BUTTON}
          </Button>
          <Button
            disabled={selectedReason === null}
            onClick={() => {
              if (
                selectedReason === AccountDeletionReason.NOT_ENOUGH_MESSAGES ||
                selectedReason === AccountDeletionReason.TOO_EXPENSIVE
              ) {
                setStep(OffboardingDialogStep.PERSUASION);
              } else if (selectedReason === AccountDeletionReason.OTHER) {
                setStep(OffboardingDialogStep.DESCRIPTION);
              } else {
                setStep(OffboardingDialogStep.CONFIRMATION);
              }
            }}
          >
            {t.SHARED.LABELS.CONTINUE_BUTTON}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function PersuasionText({
  selectedReason,
  userProfile,
  closeModal,
  setStep,
  t,
}: {
  selectedReason: AccountDeletionReason | null;
  userProfile: ReturnType<typeof useGetUserProfile>["data"];
  closeModal: () => void;
  setStep: (s: OffboardingDialogStep) => void;
  t: ReturnType<typeof useTranslation>;
}) {
  return (
    <>
      {selectedReason === AccountDeletionReason.NOT_ENOUGH_MESSAGES ? (
        <>
          <DialogHeader className="text-left mb-4">
            <DialogTitle className="text-white flex flex-row items-center">
              {t.OFFBOARDING_FORM.LABELS.MEMBERS_GROWING_TITLE}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {userProfile?.gender === UserGender.FEMALE ? (
                <>
                  {t.OFFBOARDING_FORM.LABELS.MEMBERS_GROWING_DESCRIPTION_FEMALE}
                </>
              ) : (
                <>
                  {t.OFFBOARDING_FORM.LABELS.MEMBERS_GROWING_DESCRIPTION_MALE}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="w-full flex flex-row justify-between">
              <Button variant="secondary" onClick={closeModal}>
                {t.SHARED.LABELS.CANCEL_BUTTON}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setStep(OffboardingDialogStep.CONFIRMATION)}
              >
                {t.OFFBOARDING_FORM.LABELS.CONTINUE_DELETING_BUTTON}
              </Button>
            </div>
          </DialogFooter>
        </>
      ) : (
        <>
          <DialogHeader className="text-left mb-4">
            <DialogTitle className="text-white flex flex-row items-center">
              {t.OFFBOARDING_FORM.LABELS.MEMBERS_GETTING_VALUE_TITLE}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {t.OFFBOARDING_FORM.LABELS.MEMBERS_GETTING_VALUE_DESCRIPTION}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="w-full flex flex-row justify-between">
              <Button variant="secondary" onClick={closeModal}>
                {t.SHARED.LABELS.CANCEL_BUTTON}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setStep(OffboardingDialogStep.CONFIRMATION)}
              >
                {t.OFFBOARDING_FORM.LABELS.CONTINUE_DELETING_BUTTON}
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </>
  );
}

function ConfirmationStep({
  handleDeleteMutation,
  deleteUserMutation,
  closeModal,
  t,
}: {
  handleDeleteMutation: () => void;
  deleteUserMutation: ReturnType<typeof useDeleteUser>;
  closeModal: () => void;
  t: ReturnType<typeof useTranslation>;
}) {
  return (
    <>
      <DialogHeader className="text-left mb-4">
        <DialogTitle className="text-white flex flex-row items-center">
          {t.SETTINGS_FEEDBACK_CARD.LABELS.DELETE_ACCOUNT_TITLE}
        </DialogTitle>
        <DialogDescription>
          {t.SETTINGS_FEEDBACK_CARD.LABELS.DELETE_ACCOUNT_DESCRIPTION}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <div className="w-full flex flex-row justify-between">
          <Button
            variant="destructive"
            onClick={handleDeleteMutation}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? (
              <SpinnerSmall />
            ) : (
              t.SETTINGS_FEEDBACK_CARD.LABELS.DELETE_ACCOUNT_CONFIRM
            )}
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            {t.SHARED.LABELS.CANCEL_BUTTON}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

export default function DeleteAccountButton() {
  const { toast } = useToast();
  const userProfileQuery = useGetUserProfile();
  const deleteUserMutation = useDeleteUser();
  const t = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] =
    useState<AccountDeletionReason | null>(null);
  const [reasonDescription, setReasonDescription] = useState<string>("");
  const [step, setStep] = useState<OffboardingDialogStep>(
    OffboardingDialogStep.REASON_SELECTION,
  );

  const userProfile = userProfileQuery.data;

  function handleDeleteMutation() {
    if (!selectedReason) return;
    if (selectedReason === AccountDeletionReason.OTHER && !reasonDescription)
      return;

    deleteUserMutation.mutate(
      {
        deletion_reason: selectedReason,
        deletion_reason_description: reasonDescription || null,
      },
      {
        onError: () => {
          toast({
            variant: "destructive",
            title: t.SETTINGS_FEEDBACK_CARD.ERRORS.DELETE_ACCOUNT,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      },
    );
  }

  function closeModal() {
    setDeleteModalOpen(false);
    setStep(OffboardingDialogStep.REASON_SELECTION);
    setSelectedReason(null);
    setReasonDescription("");
  }

  return (
    <>
      <Button
        variant="link"
        className="p-0 text-red-700"
        onClick={() => setDeleteModalOpen(true)}
      >
        {t.SETTINGS_FEEDBACK_CARD.LABELS.DELETE_ACCOUNT_BUTTON}
      </Button>
      <Dialog open={deleteModalOpen} onOpenChange={closeModal}>
        <DialogContent className="dark text-white focus:outline-none max-w-md max-h-[90vh] overflow-auto">
          <AnimatePresence mode="wait">
            {step === OffboardingDialogStep.REASON_SELECTION ? (
              <motion.div
                key="reason"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.1 }}
              >
                <ReasonSelection
                  selectedReason={selectedReason}
                  setSelectedReason={setSelectedReason}
                  userProfile={userProfile}
                  closeModal={closeModal}
                  setStep={setStep}
                  t={t}
                />
              </motion.div>
            ) : step === OffboardingDialogStep.PERSUASION ? (
              <motion.div
                key="persuasion"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.1 }}
              >
                <PersuasionText
                  selectedReason={selectedReason}
                  userProfile={userProfile}
                  closeModal={closeModal}
                  setStep={setStep}
                  t={t}
                />
              </motion.div>
            ) : step === OffboardingDialogStep.DESCRIPTION ? (
              <motion.div
                key="description"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.1 }}
              >
                <ReasonDescription
                  reasonDescription={reasonDescription}
                  setReasonDescription={setReasonDescription}
                  closeModal={closeModal}
                  setStep={setStep}
                  t={t}
                />
              </motion.div>
            ) : (
              <motion.div
                key="confirmation"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.1 }}
              >
                <ConfirmationStep
                  handleDeleteMutation={handleDeleteMutation}
                  deleteUserMutation={deleteUserMutation}
                  closeModal={closeModal}
                  t={t}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
}
